import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  Quote,
  QuoteCreateDto,
  QuoteResponse,
  QuoteFilters,
  QuoteSort
} from "../../modules/shared/model";
import {
  createQuote as createQuoteAction,
  updateQuote as updateQuoteAction,
  getQuoteById as getQuoteByIdAction,
  getQuotes as getQuotesAction,
  getUserQuotes as getUserQuotesAction,
  getUserQuotesWithFilters as getUserQuotesWithFiltersAction,
  getQuotesWithFilters as getQuotesWithFiltersAction,
  deleteQuote as deleteQuoteAction,
  getUserJobQuotes as getUserJobQuotesAction,
  getQuotesByJobIds as getQuotesByJobIdsAction
} from "../Actions/QuoteActions";

// Quote Async Thunks
export const createQuote = createAsyncThunk(
  "quotes/create",
  async (data: QuoteCreateDto, { rejectWithValue }) => {
    try {
      return await createQuoteAction(data);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to create quote");
    }
  }
);

export const updateQuote = createAsyncThunk(
  "quotes/update",
  async ({ id, data }: { id: string; data: Partial<QuoteCreateDto> }, { rejectWithValue }) => {
    try {
      return await updateQuoteAction(id, data);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to update quote");
    }
  }
);

export const fetchQuote = createAsyncThunk(
  "quotes/getById",
  async (id: string, { rejectWithValue }) => {
    try {
      return await getQuoteByIdAction(id);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch quote");
    }
  }
);

export const fetchQuotes = createAsyncThunk(
  "quotes/get",
  async (query: Record<string, string | number | boolean> = {}, { rejectWithValue }) => {
    try {
      return await getQuotesAction(query);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch quotes");
    }
  }
);

export const fetchQuotesByUserId = createAsyncThunk(
  "quotes/getByUserId",
  async ({ userId, query }: { userId: string; query?: Record<string, string | number | boolean> }, { rejectWithValue }) => {
    try {
      return await getUserQuotesAction(userId, query);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch user quotes");
    }
  }
);

export const fetchQuotesByUserIdWithFilters = createAsyncThunk(
  "quotes/getByUserIdWithFilters",
  async ({ userId, filters, sort, page, limit }: { userId: string; filters?: QuoteFilters; sort?: QuoteSort[]; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      return await getUserQuotesWithFiltersAction(userId, filters, sort, page, limit);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch user quotes with filters");
    }
  }
);

export const fetchQuotesWithFilters = createAsyncThunk(
  "quotes/getWithFilters",
  async ({ filters, sort, page, limit }: { filters?: QuoteFilters; sort?: QuoteSort[]; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      return await getQuotesWithFiltersAction(filters, sort, page, limit);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch quotes with filters");
    }
  }
);

export const deleteQuote = createAsyncThunk(
  "quotes/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteQuoteAction(id);
      return id;
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to delete quote");
    }
  }
);

export const fetchUserJobQuotes = createAsyncThunk(
  "quotes/getUserJobQuotes",
  async ({ userId, jobIds }: { userId: string; jobIds: string[] }, { rejectWithValue }) => {
    try {
      return await getUserJobQuotesAction(userId, jobIds);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch user job quotes");
    }
  }
);

export const fetchQuotesByJobIds = createAsyncThunk(
  "quotes/getQuotesByJobIds",
  async (jobIds: string[], { rejectWithValue }) => {
    try {
      return await getQuotesByJobIdsAction(jobIds);
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to fetch quotes by job IDs");
    }
  }
);

// Quote State Interface
interface QuoteState {
  quotes: Quote[];
  currentQuote: Quote | null;
  userQuotes: Quote[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Initial State
const initialState: QuoteState = {
  quotes: [],
  currentQuote: null,
  userQuotes: [],
  loading: false,
  error: null,
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

// Quote Slice
const quoteSlice = createSlice({
  name: "quotes",
  initialState,
  reducers: {
    clearQuotesError: (state) => {
      state.error = null;
    },
    clearCurrentQuote: (state) => {
      state.currentQuote = null;
    },
    clearUserQuotes: (state) => {
      state.userQuotes = [];
    },
    clearQuotes: (state) => {
      state.quotes = [];
      state.currentQuote = null;
      state.userQuotes = [];
      state.totalCount = 0;
      state.hasNextPage = false;
      state.hasPreviousPage = false;
    }
  },
  extraReducers: (builder) => {
    // Create Quote
    builder
      .addCase(createQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuote.fulfilled, (state, action: PayloadAction<Quote>) => {
        state.loading = false;
        state.quotes.unshift(action.payload);
        state.totalCount += 1;
        state.error = null;
      })
      .addCase(createQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Quote
    builder
      .addCase(updateQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuote.fulfilled, (state, action: PayloadAction<Quote>) => {
        state.loading = false;
        const index = state.quotes.findIndex(quote => quote.id === action.payload.id);
        if (index !== -1) {
          state.quotes[index] = action.payload;
        }
        if (state.currentQuote?.id === action.payload.id) {
          state.currentQuote = action.payload;
        }
        // Update in user quotes as well
        const userIndex = state.userQuotes.findIndex(quote => quote.id === action.payload.id);
        if (userIndex !== -1) {
          state.userQuotes[userIndex] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Quote by ID
    builder
      .addCase(fetchQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuote.fulfilled, (state, action: PayloadAction<Quote>) => {
        state.loading = false;
        state.currentQuote = action.payload;
        state.error = null;
      })
      .addCase(fetchQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get All Quotes
    builder
      .addCase(fetchQuotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotes.fulfilled, (state, action: PayloadAction<QuoteResponse>) => {
        state.loading = false;
        state.quotes = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.hasNextPage = action.payload.hasNextPage || false;
        state.hasPreviousPage = action.payload.hasPreviousPage || false;
        state.error = null;
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Quotes by User ID
    builder
      .addCase(fetchQuotesByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotesByUserId.fulfilled, (state, action: PayloadAction<QuoteResponse>) => {
        state.loading = false;
        state.userQuotes = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.hasNextPage = action.payload.hasNextPage || false;
        state.hasPreviousPage = action.payload.hasPreviousPage || false;
        state.error = null;
      })
      .addCase(fetchQuotesByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Quotes by User ID with Filters
    builder
      .addCase(fetchQuotesByUserIdWithFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotesByUserIdWithFilters.fulfilled, (state, action: PayloadAction<QuoteResponse>) => {
        state.loading = false;
        state.userQuotes = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.hasNextPage = action.payload.hasNextPage || false;
        state.hasPreviousPage = action.payload.hasPreviousPage || false;
        state.error = null;
      })
      .addCase(fetchQuotesByUserIdWithFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Quotes with Filters
    builder
      .addCase(fetchQuotesWithFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotesWithFilters.fulfilled, (state, action: PayloadAction<QuoteResponse>) => {
        state.loading = false;
        state.quotes = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.hasNextPage = action.payload.hasNextPage || false;
        state.hasPreviousPage = action.payload.hasPreviousPage || false;
        state.error = null;
      })
      .addCase(fetchQuotesWithFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete Quote
    builder
      .addCase(deleteQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteQuote.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.quotes = state.quotes.filter(quote => quote.id !== action.payload);
        state.userQuotes = state.userQuotes.filter(quote => quote.id !== action.payload);
        if (state.currentQuote?.id === action.payload) {
          state.currentQuote = null;
        }
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.error = null;
      })
      .addCase(deleteQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get User Job Quotes
    builder
      .addCase(fetchUserJobQuotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserJobQuotes.fulfilled, (state, action) => {
        state.loading = false;
        // This action is used for checking quotes, not storing them
        // The response can be used to update job status or other UI elements
        state.error = null;
      })
      .addCase(fetchUserJobQuotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Quotes by Job IDs
    builder
      .addCase(fetchQuotesByJobIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotesByJobIds.fulfilled, (state, action) => {
        state.loading = false;
        // Store the quotes data in the state
        if (Array.isArray(action.payload)) {
          state.quotes = action.payload;
        } else if (action.payload && Array.isArray(action.payload.data)) {
          state.quotes = action.payload.data;
        }
        state.error = null;
      })
      .addCase(fetchQuotesByJobIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

// Export actions
export const {
  clearQuotesError,
  clearCurrentQuote,
  clearUserQuotes,
  clearQuotes
} = quoteSlice.actions;

// Export reducer
export default quoteSlice.reducer;
